package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * Display names for the municipalities we list in, for the active [Language].
 *
 * `Address.city` / `EventVenue.city` are stored once, in their official Latin spelling — that is what
 * [Metro.matches] keys on and what a maps app needs. But a Farsi reader should not see "Richmond Hill"
 * beside a Persian business name, so every surface that *shows* a city passes it through [cityLabel].
 * English and French keep the stored spelling (official Canadian names are already bilingual-safe:
 * "Montréal", "Côte-Saint-Luc"). An unknown city falls through unchanged, so a member-typed or
 * newly-seeded city is never blanked — it just stays Latin until it is added here.
 *
 * App-owned static config, like [Metros] names: keep it next to the metro list, not in string
 * resources, because the key is data (a stored city), not a UI string id.
 */
fun Language.cityLabel(city: String): String {
    if (this != Language.FA) return city
    return FARSI_CITY_NAMES[city.cityKey()] ?: city
}

/**
 * Case/accent/punctuation-insensitive key, so "Montreal", "Montréal", "Cote Saint-Luc" and
 * "St. John's" / "St Johns" all resolve to the same entry.
 */
private fun String.cityKey(): String = buildString(length) {
    for (ch in this@cityKey.trim().lowercase()) {
        when (ch) {
            'é', 'è', 'ê', 'ë' -> append('e')
            'à', 'â' -> append('a')
            'ô' -> append('o')
            'î', 'ï' -> append('i')
            'ç' -> append('c')
            '-', '–', '.' -> append(' ')
            '\'', '’' -> Unit // "St. John's" == "St Johns"
            else -> append(ch)
        }
    }
}.replace(Regex("\\s+"), " ").trim()

private val FARSI_CITY_NAMES: Map<String, String> = mapOf(
    // ---- Greater Toronto Area ----
    "Toronto" to "تورنتو",
    "North York" to "نورث‌یورک",
    "Scarborough" to "اسکاربرو",
    "East York" to "ایست‌یورک",
    "York" to "یورک",
    "Etobicoke" to "اتوبیکو",
    "Richmond Hill" to "ریچموندهیل",
    "Thornhill" to "تورن‌هیل",
    "Vaughan" to "وان",
    "Markham" to "مارکهام",
    "Mississauga" to "میسیساگا",
    "Brampton" to "برمپتون",
    "Oakville" to "اوکویل",
    "Burlington" to "برلینگتون",
    "Aurora" to "آرورا",
    "Newmarket" to "نیومارکت",
    "Pickering" to "پیکرینگ",
    "Ajax" to "ایجکس",
    "Whitby" to "ویتبی",
    "Oshawa" to "اوشاوا",
    "Milton" to "میلتون",
    // ---- Greater Montréal ----
    "Montreal" to "مونترال",
    "Laval" to "لاوال",
    "Verdun" to "وردان",
    "Saint Laurent" to "سن‌لوران",
    "Westmount" to "وست‌مونت",
    "Pointe Claire" to "پوانت‌کلر",
    "Pierrefonds" to "پیرفون",
    "Pierrefonds Roxboro" to "پیرفون–راکسبرو",
    "La Prairie" to "لاپری",
    "Brossard" to "بروسارد",
    "Dollard des Ormeaux" to "دولارد-دز-اورمو",
    "Cote Saint Luc" to "کوت‌سن‌لوک",
    "Kirkland" to "کرکلند",
    "Dorval" to "دوروال",
    "Longueuil" to "لانگی",
    "Saint Leonard" to "سن‌لئونارد",
    "Outremont" to "اوترمون",
    "Candiac" to "کاندیاک",
    "Lachine" to "لاشین",
    "Saint Lambert" to "سن‌لامبر",
    // ---- Greater Vancouver ----
    "Vancouver" to "ونکوور",
    "North Vancouver" to "نورث ونکوور",
    "West Vancouver" to "وست ونکوور",
    "Burnaby" to "برنابی",
    "Richmond" to "ریچموند",
    "Coquitlam" to "کوکیتلام",
    "Port Coquitlam" to "پورت کوکیتلام",
    "Port Moody" to "پورت مودی",
    "New Westminster" to "نیو وست‌مینستر",
    "Surrey" to "ساری",
    "Delta" to "دلتا",
    "Langley" to "لنگلی",
    "Maple Ridge" to "میپل‌ریج",
    // ---- Ottawa–Gatineau ----
    "Ottawa" to "اتاوا",
    "Gatineau" to "گتینو",
    "Nepean" to "نپین",
    "Kanata" to "کاناتا",
    "Orleans" to "اورلئان",
    "Gloucester" to "گلاستر",
    // ---- Calgary ----
    "Calgary" to "کلگری",
    "Airdrie" to "ایردری",
    "Cochrane" to "کاکرین",
    "Chestermere" to "چسترمر",
    // ---- Smaller Canadian metros (launched 2026-10-06) + cities members commonly type ----
    "Edmonton" to "ادمونتون",
    "St Albert" to "سنت آلبرت",
    "Sherwood Park" to "شروود پارک",
    "Spruce Grove" to "اسپروس گروو",
    "Leduc" to "لدوک",
    "Winnipeg" to "وینیپگ",
    "Headingley" to "هدینگلی",
    "Halifax" to "هالیفکس",
    "Dartmouth" to "دارتموث",
    "Bedford" to "بدفورد",
    "Lower Sackville" to "لوئر ساکویل",
    "Sackville" to "ساکویل",
    "Victoria" to "ویکتوریا",
    "Saanich" to "سانیچ",
    "Langford" to "لنگفورد",
    "Esquimalt" to "اسکوایمالت",
    "Oak Bay" to "اوک بی",
    "Sidney" to "سیدنی",
    "Kitchener" to "کیچنر",
    "Waterloo" to "واترلو",
    "Cambridge" to "کمبریج",
    "Guelph" to "گوئلف",
    "London" to "لندن",
    "St Thomas" to "سنت توماس",
    "Hamilton" to "همیلتون",
    "Stoney Creek" to "استونی کریک",
    "Ancaster" to "انکستر",
    "Dundas" to "داندس",
    "Windsor" to "ویندزور",
    "Tecumseh" to "تکامسی",
    "LaSalle" to "لاسال",
    "Kingston" to "کینگستون",
    "Saskatoon" to "ساسکاتون",
    "Martensville" to "مارتنزویل",
    "Warman" to "وارمن",
    "Regina" to "رجاینا",
    "White City" to "وایت سیتی",
    "St John's" to "سنت جانز",
    "Mount Pearl" to "مونت پرل",
    "Paradise" to "پارادایس",
    "Quebec" to "کبک",
    "Quebec City" to "شهر کبک",
    // ---- Greater Los Angeles ----
    "Los Angeles" to "لس‌آنجلس",
    "Westwood" to "وست‌وود",
    "Beverly Hills" to "بورلی‌هیلز",
    "Santa Monica" to "سانتا مونیکا",
    "Brentwood" to "برنت‌وود",
    "Glendale" to "گلندیل",
    "Burbank" to "بربنک",
    "Pasadena" to "پاسادنا",
    "Encino" to "انسینو",
    "Sherman Oaks" to "شرمن اوکس",
    "Tarzana" to "تارزانا",
    "Woodland Hills" to "وودلند هیلز",
    "Irvine" to "ارواین",
    "Costa Mesa" to "کاستا میسا",
    "Newport Beach" to "نیوپورت بیچ",
    "Anaheim" to "آناهایم",
    "Santa Ana" to "سانتا آنا",
    "Tustin" to "تاستین",
    "Laguna Hills" to "لاگونا هیلز",
    "Mission Viejo" to "میشن ویه‌هو",
    "Torrance" to "تورنس",
    "Orange" to "اورنج",
    // ---- SF Bay Area ----
    "San Francisco" to "سان‌فرانسیسکو",
    "San Jose" to "سن‌خوزه",
    "Cupertino" to "کوپرتینو",
    "Sunnyvale" to "سانی‌ویل",
    "Santa Clara" to "سانتا کلارا",
    "Mountain View" to "مانتین ویو",
    "Palo Alto" to "پالو آلتو",
    "Fremont" to "فریمانت",
    "Oakland" to "اوکلند",
    "Berkeley" to "برکلی",
    "San Mateo" to "سن‌متئو",
    "Walnut Creek" to "والنات کریک",
    "Belmont" to "بلمونت",
    "Danville" to "دنویل",
    "Albany" to "آلبانی",
    // ---- Washington DC Metro ----
    "Washington" to "واشنگتن",
    "Bethesda" to "بتزدا",
    "Rockville" to "راک‌ویل",
    "Vienna" to "وینا",
    "McLean" to "مک‌لین",
    "Arlington" to "آرلینگتون",
    "Alexandria" to "الکساندریا",
    "Ashburn" to "اشبرن",
    "Sterling" to "استرلینگ",
    "Fairfax" to "فیرفکس",
    "Reston" to "رستون",
    // ---- New York Metro ----
    "New York" to "نیویورک",
    "Manhattan" to "منهتن",
    "Brooklyn" to "بروکلین",
    "Queens" to "کوئینز",
    "Great Neck" to "گریت‌نک",
    "Roslyn" to "رازلین",
    "Englewood" to "انگلوود",
    "Ridgewood" to "ریج‌وود",
    "White Plains" to "وایت پلینز",
    "Morristown" to "موریس‌تاون",
    "Paterson" to "پترسون",
    "Oakland Gardens" to "اوکلند گاردنز",
    "Greenvale" to "گرین‌ویل",
    // ---- Seattle Metro ----
    "Seattle" to "سیاتل",
    "Bellevue" to "بلویو",
    "Redmond" to "ردموند",
    "Sammamish" to "سمامیش",
    // ---- Texas + San Diego ----
    "Houston" to "هیوستون",
    "Sugar Land" to "شوگر لند",
    "Katy" to "کیتی",
    "The Woodlands" to "وودلندز",
    "Dallas" to "دالاس",
    "Plano" to "پلینو",
    "Richardson" to "ریچاردسون",
    "Frisco" to "فریسکو",
    "Fort Worth" to "فورت‌ورث",
    "San Diego" to "سن‌دیگو",
    "La Jolla" to "لاهویا",
).mapKeys { it.key.cityKey() }
